import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircleOutline,
  Home,
} from '@mui/icons-material';
import { submitAssessment } from '../../utils/api';
import ChangeLanguage from '../sections/ChangeLanguage';

function AssessmentResult() {
  const { assessmentId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const navigate = useNavigate();
  const location = useLocation();
  
  const [submitting, setSubmitting] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    const submitAnswers = async (answersArray, totalTime, questionTimes) => {
      try {
        setSubmitting(true);
        setError(null);

        console.log('Submitting answers:', {
          assessmentId,
          taskId,
          answers: answersArray,
          totalTime,
          questionTimes,
        });

        // Call backend API to submit answers with timer data
        const result = await submitAssessment(
          assessmentId,
          answersArray,
          taskId,
          totalTime,
          questionTimes
        );

        console.log('Assessment submitted successfully:', result);
        setSuccess(true);
      } catch (error) {
        console.error('Error submitting answers:', error);
        setError(error.message || t('AssessmentResult.failedToSubmit'));
      } finally {
        setSubmitting(false);
      }
    };

    // Prevent duplicate submissions
    if (hasSubmitted) {
      return;
    }

    // Get answers and timer data from navigation state
    const answers = location.state?.answers;
    const totalTime = location.state?.totalTime;
    const questionTimes = location.state?.questionTimes;
    
    if (!answers) {
      setError(t('AssessmentResult.noAnswersFound'));
      setSubmitting(false);
      return;
    }

    setHasSubmitted(true);
    submitAnswers(answers, totalTime, questionTimes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReturnHome = () => {
    navigate('/student-home');
  };

  const handleReturnToDetail = () => {
    navigate(`/student/assessment/${assessmentId}${taskId ? `?taskId=${taskId}` : ''}`);
  };

  if (submitting) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <ChangeLanguage buttonColor="#4e7ba8" />
          </Box>
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <CircularProgress size={60} sx={{ mb: 3, color: '#4e7ba8' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {t('AssessmentResult.submittingAnswers')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('AssessmentResult.pleaseWait')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <ChangeLanguage buttonColor="#4e7ba8" />
          </Box>
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleReturnHome}
                startIcon={<Home />}
              >
                {t('AssessmentResult.returnHome')}
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate(-1)}
                sx={{ bgcolor: '#4e7ba8', '&:hover': { bgcolor: '#3d5f85' } }}
              >
                {t('AssessmentResult.tryAgain')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <ChangeLanguage buttonColor="#4e7ba8" />
          </Box>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)',
            }}
          >
            {/* Success Icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 60, color: '#ffffff' }} />
            </Box>

            {/* Thank You Message */}
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#2c3e50' }}>
              {t('AssessmentResult.thankYou')}
            </Typography>
            
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: '#4e7ba8' }}>
              {t('AssessmentResult.completedSuccessfully')}
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8, whiteSpace: 'pre-line' }}>
              {t('AssessmentResult.completionMessage')}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleReturnToDetail}
                sx={{
                  minWidth: 180,
                  borderColor: '#4e7ba8',
                  color: '#4e7ba8',
                  '&:hover': {
                    borderColor: '#3d5f85',
                    bgcolor: 'rgba(78, 123, 168, 0.04)',
                  },
                }}
              >
                {t('AssessmentResult.returnToAssessment')}
              </Button>
              <Button
                variant="contained"
                onClick={handleReturnHome}
                startIcon={<Home />}
                sx={{
                  minWidth: 180,
                  bgcolor: '#4e7ba8',
                  '&:hover': { bgcolor: '#3d5f85' },
                }}
              >
                Return Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return null;
}

export default AssessmentResult;
